import React from 'react';
import { X, Copy, Share2, Download } from 'lucide-react';
import { User } from '../App';
import { toast } from 'sonner';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
}

export function ShareModal({ isOpen, onClose, user }: ShareModalProps) {
  if (!isOpen) return null;

  const copyToClipboard = async (text: string, message: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(message);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success(message);
    }
  };

  const shareData = {
    referralCode: user.referralCode,
    walletAddress: user.walletAddress,
    downloadLink: 'https://wallet.vatton.app/'
  };

  const handleShare = async () => {
    const shareText = `🚀 Join Vatton Wallet!\n\n📱 Use my referral code: ${shareData.referralCode}\n💰 Start earning rewards today!\n\n🔗 Download: ${shareData.downloadLink}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Vatton Wallet',
          text: shareText,
        });
      } catch (error) {
        copyToClipboard(shareText, 'Share text copied to clipboard!');
      }
    } else {
      copyToClipboard(shareText, 'Share text copied to clipboard!');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-card rounded-3xl p-6 w-full max-w-sm shadow-2xl border border-border">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-foreground">Share Vatton Wallet</h3>
          <button
            onClick={onClose}
            className="p-2 text-muted-foreground hover:text-foreground transition-colors rounded-xl hover:bg-muted"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Referral Code */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">Referral Code</span>
              <button
                onClick={() => copyToClipboard(shareData.referralCode, 'Referral code copied!')}
                className="p-1 text-indigo-600 hover:text-indigo-700 transition-colors"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <div className="text-2xl font-bold text-indigo-600 tracking-wider">
              {shareData.referralCode}
            </div>
          </div>

          {/* Wallet Address */}
          <div className="bg-muted rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">Wallet Address</span>
              <button
                onClick={() => copyToClipboard(shareData.walletAddress, 'Wallet address copied!')}
                className="p-1 text-primary hover:opacity-80 transition-opacity"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <div className="text-sm font-mono text-foreground break-all">
              {shareData.walletAddress}
            </div>
          </div>

          {/* Download Link */}
          <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">Download Link</span>
              <button
                onClick={() => copyToClipboard(shareData.downloadLink, 'Download link copied!')}
                className="p-1 text-emerald-600 hover:text-emerald-700 transition-colors"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <div className="text-sm text-emerald-600 font-medium">
              {shareData.downloadLink}
            </div>
          </div>

          {/* Share Button */}
          <button
            onClick={handleShare}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-2xl font-medium hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg"
          >
            <Share2 className="w-5 h-5" />
            Share Vatton Wallet
          </button>
        </div>
      </div>
    </div>
  );
}